/**
 * API Service for making backend API calls
 * All API calls should use this utility to ensure consistent base URL and authentication
 */

import { getToken } from "./session";
import { muteMicForApi, unmuteMicForApi } from "./micMuteRegistry";
import {
  beginGlobalApiLoading,
  endGlobalApiLoading,
} from "./globalApiLoading";

// Base API URL from environment variable (without /api suffix)
// Example: https://localhost:58409
// The /api prefix will be added automatically for all API endpoints
const API_BASE_URL = 'https://ameenaiintegration.eg-insurtech.com';
// const API_BASE_URL = "https://localhost:58409";
// const API_BASE_URL = 'https://localhost:58409';
const API_PREFIX = "/api";

/**
 * Endpoints exempt from automatic mic muting.
 * These calls happen silently in the background and must not interrupt voice input.
 */
const MIC_MUTE_EXEMPT_ENDPOINTS = ["/UserSessionDraft/save"];

/** Background or high-frequency calls — no full-screen global loader */
const GLOBAL_LOADING_EXEMPT_PATHS = ["/UserSessionDraft/save"];

function shouldSkipGlobalLoading(
  endpoint: string,
  skipFromOptions?: boolean,
): boolean {
  if (skipFromOptions) return true;
  return GLOBAL_LOADING_EXEMPT_PATHS.some((p) => endpoint.includes(p));
}

export type ApiFetchRequestInit = RequestInit & {
  skipGlobalLoading?: boolean;
};

export interface LoginRequest {
  mobile: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  userName: string;
  fullName: string;
  mobile: string;
  email: string;
}

export interface RegisterRequest {
  fullName: string;
  mobile: string;
  password: string;
  confirmPassword: string;
}

export interface RegisterResponse {
  token: string;
  userName: string;
  fullName: string;
  mobile: string;
  email: string;
}

export interface ApiError {
  message: string;
  statusCode?: number;
}

/**
 * Creates headers for API requests, automatically including the auth token if available
 */
function getAuthHeaders(includeAuth: boolean = true): HeadersInit {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (includeAuth) {
    const token = getToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  return headers;
}

/**
 * Gets the full API base URL with /api prefix
 */
export function getApiBaseUrl(): string {
  return `${API_BASE_URL}${API_PREFIX}`;
}

/**
 * Generic API fetch function that automatically includes authentication token
 * All API calls should use this function to ensure consistent base URL and auth headers
 *
 * @param endpoint - API endpoint path (e.g., '/Auth/login' or '/Policy/calculate-premium')
 * @param options - Fetch options (method, body, headers, etc.)
 * @param includeAuth - Whether to include authentication token (default: true)
 * @returns Promise<Response>
 *
 * @example
 * const response = await apiFetch('/Policy/calculate-premium', {
 *   method: 'POST',
 *   body: JSON.stringify(data)
 * });
 */
export async function apiFetch(
  endpoint: string,
  options: ApiFetchRequestInit = {},
  includeAuth: boolean = true,
): Promise<Response> {
  const { skipGlobalLoading, ...fetchOptions } = options;

  // If endpoint is already a full URL, use it as-is
  if (endpoint.startsWith("http://") || endpoint.startsWith("https://")) {
    const headers = {
      ...getAuthHeaders(includeAuth),
      ...fetchOptions.headers,
    };
    const shouldMute = !MIC_MUTE_EXEMPT_ENDPOINTS.some((e) =>
      endpoint.includes(e),
    );
    const showGlobal = !shouldSkipGlobalLoading(endpoint, skipGlobalLoading);
    if (shouldMute) muteMicForApi();
    if (showGlobal) beginGlobalApiLoading();
    try {
      return await fetch(endpoint, { ...fetchOptions, headers });
    } finally {
      if (showGlobal) endGlobalApiLoading();
      if (shouldMute) unmuteMicForApi();
    }
  }

  // Ensure endpoint starts with /
  const normalizedEndpoint = endpoint.startsWith("/")
    ? endpoint
    : `/${endpoint}`;
  const url = `${getApiBaseUrl()}${normalizedEndpoint}`;

  const headers = {
    ...getAuthHeaders(includeAuth),
    ...fetchOptions.headers,
  };

  const shouldMute = !MIC_MUTE_EXEMPT_ENDPOINTS.some((e) =>
    normalizedEndpoint.includes(e),
  );
  const showGlobal = !shouldSkipGlobalLoading(
    normalizedEndpoint,
    skipGlobalLoading,
  );
  if (shouldMute) muteMicForApi();
  if (showGlobal) beginGlobalApiLoading();
  try {
    return await fetch(url, { ...fetchOptions, headers });
  } finally {
    if (showGlobal) endGlobalApiLoading();
    if (shouldMute) unmuteMicForApi();
  }
}

export interface AuthRequestOptions {
  /** When false, full-app minimal spinner runs (e.g. silent auto-login). When true, hide it — use inline button spinner on login UI. */
  skipGlobalLoading?: boolean;
}

/**
 * Makes a login request to the backend API (no auth token needed)
 */
export async function login(
  credentials: LoginRequest,
  options?: AuthRequestOptions,
): Promise<LoginResponse> {
  try {
    const response = await apiFetch(
      "/Auth/login",
      {
        method: "POST",
        body: JSON.stringify({
          Mobile: credentials.mobile,
          Password: credentials.password,
        }),
        skipGlobalLoading: options?.skipGlobalLoading ?? false,
      },
      false,
    );

    if (!response.ok) {
      let errorMessage = "Login failed";
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = `Login failed with status ${response.status}`;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return {
      token: data.token,
      userName: data.userName,
      fullName: data.fullName || "",
      mobile: data.mobile || "",
      email: data.email || "",
    };
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unexpected error occurred during login");
  }
}

/**
 * Makes a registration request to the backend API (no auth token needed)
 */
export async function register(
  data: RegisterRequest,
  options?: AuthRequestOptions,
): Promise<RegisterResponse> {
  try {
    const response = await apiFetch(
      "/Auth/register",
      {
        method: "POST",
        body: JSON.stringify({
          FullName: data.fullName,
          Mobile: data.mobile,
          Password: data.password,
          ConfirmPassword: data.confirmPassword,
        }),
        skipGlobalLoading: options?.skipGlobalLoading ?? false,
      },
      false,
    );

    if (!response.ok) {
      let errorMessage = "Registration failed";
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = `Registration failed with status ${response.status}`;
      }
      throw new Error(errorMessage);
    }

    const responseData = await response.json();
    return {
      token: responseData.token,
      userName: responseData.userName,
      fullName: responseData.fullName || "",
      mobile: responseData.mobile || "",
      email: responseData.email || "",
    };
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unexpected error occurred during registration");
  }
}
